import { UiSchema } from '@rjsf/core';
import { JSONSchema7 } from 'json-schema';
import { range } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IntakeForm } from 'views/pages/client-record/useIntakeForm';
import JsonFormContainer from '../JsonFormContainer';
import Actions from './Actions';

const uiSchema: UiSchema = {
  socialHistory_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  socialHistory_2: { 'ui:widget': 'textarea' },
  socialHistory_3: { 'ui:widget': 'textarea' },
  socialHistory_6: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  socialHistory_7: { 'ui:widget': 'textarea' },
  socialHistory_8: { 'ui:widget': 'textarea' },
  socialHistory_10: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  socialHistory_12: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  socialHistory_15: { 'ui:widget': 'textarea' }
};

interface Props {
  intakeForm: IntakeForm;
  onPrevious?: () => void;
  isSubmitting: boolean;
  onSubmit: (data: any) => void;
}

export default function SocialHistory({ intakeForm, onPrevious, isSubmitting, onSubmit }: Props) {
  const intl = useIntl();
  const schema: JSONSchema7 = {
    type: 'object',
    title: intl.formatMessage({ defaultMessage: 'Social History' }),
    properties: {
      socialHistory_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Were you adopted?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      socialHistory_2: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Where did you grow up?' })
      },
      socialHistory_3: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'List your siblings and their ages:' })
      },
      socialHistory_4: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: "What was your father's occupation?" })
      },
      socialHistory_5: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: "What was your mother's occupation?" })
      },
      socialHistory_6: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: "Did your parents' divorce?" }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      socialHistory_6_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If so, how old were you when they divorced?' })
      },
      socialHistory_6_2: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If your parents divorced, who did you live with?' })
      },
      socialHistory_7: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Describe your father and your relationship with him:' })
      },
      socialHistory_8: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Describe your mother and your relationship with her:' })
      },
      socialHistory_9: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'How old were you when you left home?' })
      },
      socialHistory_10: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Has anyone in your immediate family died?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      socialHistory_11: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Who and when?' })
      },
      socialHistory_12: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Are you currently in a romantic relationship?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      socialHistory_13: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If yes, how long have you been in this relationship?' })
      },
      socialHistory_14: {
        type: 'number',
        title: intl.formatMessage({
          defaultMessage: 'On a scale of 1-10 (10 being the highest quality), how would you rate your current relationship?'
        }),
        enum: [intl.formatMessage({ defaultMessage: 'No Apply' }), ...range(1, 11)]
      },
      socialHistory_15: {
        type: 'string',
        title: intl.formatMessage({
          defaultMessage: 'In the last year, have you experienced any significant life changes or stressors? If yes, please explain:'
        })
      }
    }
  };

  const [formData, setFormData] = useState(intakeForm.socialHistory);

  return (
    <JsonFormContainer
      noValidate
      schema={schema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={({ formData }) => setFormData(formData)}
      onSubmit={({ formData }) =>
        onSubmit({
          socialHistory: formData
        })
      }
    >
      <Actions onPrevious={onPrevious} disabled={isSubmitting} />
    </JsonFormContainer>
  );
}
